import { z } from 'zod'

import { toE164 } from '@/lib/format'

import type { GroupOrder } from './schema'
import type { OrderSubTabKey } from './order-tabs'

export const billingRuleSchema = z.enum(['SENDER', 'RECEIVER'])

/** @deprecated Use `billingRuleSchema` */
export const insuranceBillingRuleSchema = billingRuleSchema

/** algo-pos-be `maxOrderTotalWeightGram` — create manual + edit. */
export const MAX_ORDER_TOTAL_WEIGHT_GRAMS = 400_000
/** @deprecated Use `MAX_ORDER_TOTAL_WEIGHT_GRAMS` */
export const MAX_CHARGE_WEIGHT_GRAMS = MAX_ORDER_TOTAL_WEIGHT_GRAMS

export const editGroupOrderItemSchema = z.object({
  product: z.object({
    brand: z.string().optional(),
    name: z.string(),
    fee: z.number(),
    quantity: z.number().int(),
    actual_quantity: z.number().int(),
    has_bought: z.boolean(),
    is_unclear: z.boolean(),
    image_urls: z.string().optional(),
  }),
})

export const editGroupOrderSchema = z.object({
  total_weight_gram: z.number().max(MAX_ORDER_TOTAL_WEIGHT_GRAMS),
  height: z.number(),
  width: z.number(),
  length: z.number(),
  order_receiver: z.object({
    name: z.string(),
  }),
  order_items: z.array(editGroupOrderItemSchema).optional(),
})

/** API-FE-03: create a manual unpaid order from the Kelola Grup form. */
export const createGroupOrderSchema = editGroupOrderSchema.extend({
  order_receiver: z.object({
    name: z.string(),
    phone_number: z.string(),
  }),
})

export const createGroupOrderResponseSchema = z
  .object({
    external_id: z.string().optional(),
  })
  .passthrough()

export const chargeGroupOrderSchema = z.object({
  insurance_billing_rule: billingRuleSchema,
  shipping_billing_rule: billingRuleSchema,
  discount_price: z.number().min(0),
})

export const chargeGroupOrderResponseSchema = z.object({
  short_link: z.string(),
})

export type BillingRule = z.infer<typeof billingRuleSchema>
export type InsuranceBillingRule = BillingRule
export type EditGroupOrderInput = z.infer<typeof editGroupOrderSchema>
export type CreateGroupOrderInput = z.infer<typeof createGroupOrderSchema>
export type CreateGroupOrderResponse = z.infer<typeof createGroupOrderResponseSchema>
export type ChargeGroupOrderInput = z.infer<typeof chargeGroupOrderSchema>

export interface UnpaidOrderDraft {
  buyerName: string
  /** Local subscriber digits (no +62). Required when creating a manual order. */
  phone?: string
  weight: number
  length: number
  width: number
  height: number
  items: ReadonlyArray<{
    name: string
    unitPrice: number
    quantity: number
  }>
}

const DEFAULT_DIMENSION = 10
/** Default package weight (grams) for charge/tariff when none is set on the order. */
export const DEFAULT_CHARGE_WEIGHT_GRAMS = 200

/** Matches android-consumer `LioJastipCreateOrderBottomSheet` companion constants. */
export const MIN_BUYER_NAME_LENGTH = 3
export const MAX_BUYER_NAME_LENGTH = 50
export const MIN_CREATE_PHONE_DIGITS = 7
export const MAX_CREATE_PHONE_DIGITS = 16
export const MAX_PACKAGE_DIMENSION_CM = 100

function localPhoneDigits(phone: string | undefined) {
  return (phone ?? '').replace(/\D/g, '').replace(/^0+/, '')
}

function namedDraftItems(draft: UnpaidOrderDraft) {
  return draft.items.filter((item) => item.name.trim().length > 0)
}

function defaultWeightGrams(draft: UnpaidOrderDraft) {
  const quantity = namedDraftItems(draft).reduce((sum, item) => sum + item.quantity, 0)
  const estimated = draft.weight || DEFAULT_CHARGE_WEIGHT_GRAMS * Math.max(1, quantity)
  return Math.min(estimated, MAX_ORDER_TOTAL_WEIGHT_GRAMS)
}

export const MIN_ITEM_UNIT_PRICE = 1_000
export const MAX_ITEM_UNIT_PRICE = 999_999_999
/** Matches android-consumer `MAX_PRODUCT_NAME_LENGTH`. */
export const MAX_PRODUCT_NAME_LENGTH = 100

export function isValidItemUnitPrice(price: number) {
  return price >= MIN_ITEM_UNIT_PRICE && price <= MAX_ITEM_UNIT_PRICE
}

export function parseItemUnitPriceInput(raw: string) {
  const parsed = Number(raw.replace(/\D/g, '')) || 0
  return Math.min(MAX_ITEM_UNIT_PRICE, parsed)
}

export function itemUnitPriceError(price: number) {
  if (price <= 0) return 'Harga wajib diisi'
  if (price < MIN_ITEM_UNIT_PRICE) return 'Harga minimal Rp1.000'
  if (price > MAX_ITEM_UNIT_PRICE) return 'Harga maksimal Rp999.999.999'
  return null
}

export function itemNameError(name: string) {
  if (name.trim().length === 0) return 'Nama item wajib diisi'
  if (name.length > MAX_PRODUCT_NAME_LENGTH) return 'Maks. 100 karakter'
  return null
}

export function buildEditOrderPayload(draft: UnpaidOrderDraft): EditGroupOrderInput {
  return {
    total_weight_gram: draft.weight,
    height: draft.height || DEFAULT_DIMENSION,
    width: draft.width || DEFAULT_DIMENSION,
    length: draft.length || DEFAULT_DIMENSION,
    order_receiver: { name: draft.buyerName.trim() },
    order_items: draft.items
      .filter((item) => item.name.trim().length > 0)
      .map((item) => ({
        product: {
          brand: '',
          name: item.name.trim(),
          fee: item.unitPrice,
          quantity: item.quantity,
          actual_quantity: item.quantity,
          has_bought: true,
          is_unclear: false,
          image_urls: '',
        },
      })),
  }
}

/** Same normalization as the unpaid detail editor, from server order data. */
export function orderToUnpaidDraft(order: GroupOrder): UnpaidOrderDraft {
  return {
    buyerName: order.receiverName ?? '',
    weight: order.totalWeightGrams ?? 0,
    length: order.length ?? 0,
    width: order.width ?? 0,
    height: order.height ?? 0,
    items: order.items.map((item) => ({
      name: item.isUnclear && item.name === 'Produk tanpa nama' ? '' : item.name,
      unitPrice: item.unitPrice,
      quantity: item.quantity || 1,
    })),
  }
}

export function buildEditOrderPayloadFromOrder(order: GroupOrder) {
  return buildEditOrderPayload(orderToUnpaidDraft(order))
}

export function hasChargeWeight(weightGrams: number) {
  return weightGrams > 0 && weightGrams <= MAX_ORDER_TOTAL_WEIGHT_GRAMS
}

function hasValidBuyerAndItems(draft: UnpaidOrderDraft) {
  const items = namedDraftItems(draft)
  return (
    draft.buyerName.trim().length > 0 &&
    items.length > 0 &&
    items.every((item) => isValidItemUnitPrice(item.unitPrice) && item.quantity > 0)
  )
}

export function canSendInvoice(draft: UnpaidOrderDraft) {
  return hasChargeWeight(draft.weight) && hasValidBuyerAndItems(draft)
}

function createPhoneDigits(phone: string | undefined) {
  return (phone ?? '').replace(/\D/g, '')
}

function hasValidCreateBuyerName(name: string) {
  const length = name.trim().length
  return length >= MIN_BUYER_NAME_LENGTH && length <= MAX_BUYER_NAME_LENGTH
}

function hasValidCreatePhone(phone: string | undefined) {
  const length = createPhoneDigits(phone).length
  return length >= MIN_CREATE_PHONE_DIGITS && length <= MAX_CREATE_PHONE_DIGITS
}

function hasValidCreateDimensions(draft: UnpaidOrderDraft) {
  return [draft.length, draft.width, draft.height].every(
    (value) => value >= 0 && value <= MAX_PACKAGE_DIMENSION_CM,
  )
}

function isValidCreateItem(item: UnpaidOrderDraft['items'][number]) {
  return (
    item.name.trim().length > 0 &&
    item.name.length <= MAX_PRODUCT_NAME_LENGTH &&
    isValidItemUnitPrice(item.unitPrice) &&
    item.quantity > 0
  )
}

/** Same enable rules as android-consumer `LioJastipCreateOrderBottomSheet.isFormValid`. */
export function canCreateOrder(draft: UnpaidOrderDraft) {
  return (
    hasValidCreateBuyerName(draft.buyerName) &&
    hasValidCreatePhone(draft.phone) &&
    hasChargeWeight(draft.weight) &&
    hasValidCreateDimensions(draft) &&
    draft.items.some(isValidCreateItem)
  )
}

export function buildCreateOrderPayload(draft: UnpaidOrderDraft): CreateGroupOrderInput {
  const edit = buildEditOrderPayload({ ...draft, items: draft.items.filter(isValidCreateItem) })
  return {
    ...edit,
    total_weight_gram: defaultWeightGrams(draft),
    order_receiver: {
      name: draft.buyerName.trim(),
      phone_number: toE164('+62', localPhoneDigits(draft.phone)),
    },
  }
}

export function shortOrderRef(id: string) {
  return `#${id.replaceAll('-', '').slice(0, 8).toUpperCase()}`
}

interface SubtotalLine {
  unitPrice: number
  quantity: number
  actualQuantity?: number
}

export function computeOrderSubtotal(items: ReadonlyArray<SubtotalLine>, fallback = 0) {
  const sum = items.reduce(
    (total, item) => total + item.unitPrice * (item.actualQuantity || item.quantity || 1),
    0,
  )
  return sum || fallback
}

export interface InvoiceChargeTariffInput {
  weightGrams: number
  length: number
  width: number
  height: number
  goodsValue: number
  receiverDistrict: string | null
}

/** Tagih Ulang is only available after the invoice is no longer pending (WEBLP-2232). */
export function canShowResendCharge(order: GroupOrder, subTab: OrderSubTabKey) {
  return subTab === 'all' && order.invoiceStatus !== 'PENDING'
}

/** Cancel is not allowed while an invoice is waiting for payment (WEBLP-2494). */
export function canCancelBilledOrder(subTab: OrderSubTabKey) {
  return subTab !== 'awaiting-payment'
}

/** Resend charge payload from values already stored on the order (Tagih Ulang). */
export function buildResendChargePayload(order: GroupOrder): ChargeGroupOrderInput {
  return {
    insurance_billing_rule: order.insuranceBillingRule ?? 'RECEIVER',
    shipping_billing_rule: order.shippingBillingRule ?? 'RECEIVER',
    discount_price: order.discountPrice,
  }
}

export function buildInvoiceTariffInput(input: {
  weightGrams: number
  length?: number | null
  width?: number | null
  height?: number | null
  goodsValue: number
  receiverDistrict?: string | null
}): InvoiceChargeTariffInput {
  return {
    weightGrams: input.weightGrams,
    length: input.length || DEFAULT_DIMENSION,
    width: input.width || DEFAULT_DIMENSION,
    height: input.height || DEFAULT_DIMENSION,
    goodsValue: input.goodsValue,
    receiverDistrict: input.receiverDistrict?.trim() || null,
  }
}
